/**
 * Tag Selector with auto-search and create-on-enter functionality
 *
 * Features:
 * - Multi-select tags
 * - Type to search existing tags
 * - Enter to select matching tag
 * - Enter to create new tag if no match
 * - Shows tag color badges
 */

import { useState, useCallback, useMemo } from 'react';
import { Select, Spin, Space, message } from 'antd';
import { PlusOutlined, TagsOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tagsApi } from '@api/index';
import { StyledTag } from '@components/common';
import type { Tag as TagType } from '@/types';

interface TagSelectorProps {
  value?: string[];
  onChange?: (value: string[]) => void;
  placeholder?: string;
  allowCreate?: boolean;
  disabled?: boolean;
  maxTags?: number;
}

export default function TagSelector({
  value = [],
  onChange,
  placeholder = 'Search or create tags...',
  allowCreate = true,
  disabled = false,
  maxTags,
}: TagSelectorProps) {
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState('');

  const { data: tags = [], isLoading } = useQuery({
    queryKey: ['tags'],
    queryFn: () => tagsApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => tagsApi.create({ name }),
    onSuccess: (newTag) => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      onChange?.([...value, newTag.id]);
      message.success(`Tag "${newTag.name}" created`);
      setSearchValue('');
    },
    onError: () => {
      message.error('Failed to create tag');
    },
  });

  const filteredTags = useMemo(() => {
    if (!searchValue) return tags;
    const lower = searchValue.toLowerCase();
    return tags.filter((tag) => tag.name.toLowerCase().includes(lower));
  }, [tags, searchValue]);

  const exactMatch = useMemo(() => {
    if (!searchValue) return null;
    const lower = searchValue.toLowerCase();
    return tags.find((tag) => tag.name.toLowerCase() === lower);
  }, [tags, searchValue]);

  const handleSearch = useCallback((val: string) => {
    setSearchValue(val);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && searchValue && !exactMatch && allowCreate) {
        e.preventDefault();
        e.stopPropagation();
        createMutation.mutate(searchValue);
      }
    },
    [searchValue, exactMatch, allowCreate, createMutation]
  );

  const handleChange = useCallback(
    (selectedValues: string[]) => {
      // Check for "create new" special value
      const createValue = selectedValues.find((v) => v.startsWith('__create__:'));
      if (createValue) {
        const newName = createValue.replace('__create__:', '');
        createMutation.mutate(newName);
        return;
      }

      // Enforce maxTags if specified
      if (maxTags && selectedValues.length > maxTags) {
        message.warning(`Maximum ${maxTags} tags allowed`);
        return;
      }

      onChange?.(selectedValues);
      setSearchValue('');
    },
    [onChange, maxTags, createMutation]
  );

  const options = useMemo(() => {
    const tagOptions = filteredTags.map((tag) => ({
      value: tag.id,
      label: tag.name,
      tag,
    }));

    // Add "create new" option if searching and no exact match
    if (searchValue && !exactMatch && allowCreate) {
      tagOptions.push({
        value: `__create__:${searchValue}`,
        label: `Create "${searchValue}"`,
        tag: null as any,
      });
    }

    return tagOptions;
  }, [filteredTags, searchValue, exactMatch, allowCreate]);

  const tagRender = useCallback(
    (props: { value: string; label: React.ReactNode; closable: boolean; onClose: () => void }) => {
      const tag = tags.find((t) => t.id === props.value);
      return (
        <StyledTag
          name={tag?.name || String(props.label)}
          color={tag?.color || '#d9d9d9'}
          closable={props.closable}
          onClose={props.onClose}
        />
      );
    },
    [tags]
  );

  return (
    <Select
      mode="multiple"
      showSearch
      allowClear
      value={value}
      placeholder={placeholder}
      disabled={disabled || createMutation.isPending}
      loading={isLoading || createMutation.isPending}
      filterOption={false}
      searchValue={searchValue}
      onSearch={handleSearch}
      onChange={handleChange}
      onInputKeyDown={handleKeyDown}
      options={options}
      tagRender={tagRender}
      notFoundContent={isLoading ? <Spin size="small" /> : null}
      style={{ width: '100%' }}
      suffixIcon={<TagsOutlined />}
      optionRender={(option) => {
        if (option.value?.toString().startsWith('__create__:')) {
          return (
            <Space style={{ color: '#1890ff' }}>
              <PlusOutlined />
              {option.label}
            </Space>
          );
        }
        const tag = (option.data as any).tag as TagType;
        return (
          <Space>
            <span
              style={{
                display: 'inline-block',
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: tag?.color || '#d9d9d9',
              }}
            />
            {option.label}
          </Space>
        );
      }}
    />
  );
}
